<template>
  <BaseWidget
    :deviceIds="deviceIds"
    :widgetId="widgetId"
    :widgetType="widgetType"
    :widgetMaxDevices="widgetMaxDevices"
    :title="name"
    :subtitle="deviceType"
    class="history-widget"
    :fullscreen="true"
    :observeSize="true"
    @fullscreenChange="fullscreenChange"
    @removeDevice="removeDevice"
    @removeWidget="removeWidget"
    @widgetSizeChanged="updateWidgetSize"
    @refreshDashboard="refreshDashboard"
    data-cy="HistoryWidget"
  >
    <template v-slot:actions>
      <HistoryMenuItems
        :selectedDevices="graph.selectedDevices"
        :passedinParameter="selectedParameter"
        :widgetId="widgetId"
        :permissions="permissions"
        @parameterChange="parameterChange"
        @openExportData="openExportData"
      />
    </template>
    <template v-slot:content>
      <HistoryGraph
        class="history-widget-content"
        ref="historyGraph"
        :allowMonth="false"
        :allowWeek="false"
        :allowThreeMonths="false"
        :allowCustom="false"
        :isFullscreen="graph.isFullscreen"
        :isDetachedDialog="graph.isDetachedDialog"
        :removableDevices="graph.removableDevices"
        :selectedDevices="graph.selectedDevices"
        :selectedParameter="selectedParameter"
        :selectedTimePeriod="graph.timePeriod"
        :width="dimensions.width"
        :height="dimensions.height"
        :permissions="permissions"
        @graphPeriodChanged="updateTimePeriod"
        @removeDevice="removeDevice"
      />
      <ExportDataDialog
        :open="exportData"
        :devices="graph.selectedDevices"
        :permissions="permissions"
        @close="closeExportData"
      />
    </template>
    <template v-slot:left-toolbar>
      <div class="toolbar">
        <v-btn variant="text" @click="refresh" data-cy="refreshHistory">
          <v-icon color="grey" icon="mdi-refresh" />
        </v-btn>
      </div>
    </template>
  </BaseWidget>
</template>

<script lang="ts">
import { defineComponent, nextTick, computed, reactive, toRefs, type PropType } from 'vue'
import { useStore } from 'vuex'
import BaseWidget from './BaseWidget.vue'
import HistoryGraph from '../history/HistoryGraph.vue'
import ExportDataDialog from '@/components/dialogs/ExportDataDialog.vue'
import { saveWidgetSettings } from '@/services/dashboard'
import HistoryMenuItems from '../history/HistoryMenuItems.vue'
import { deviceModelDisplay } from '@/services/map'
import { getPermissionsByDeviceId } from '@/services/subscriptions'
import {
  useMeasurementOptions,
  type MeasurementOptionsProps
} from '@/composables/widgets/useMeasurementOptions'
import { NAMESPACES, type RootState } from '@/store'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { MAP_GETTERS } from '@/store/map'
import type { WidgetSettings } from '@/types/dashboard'
import type { WidgetDevice } from '@/helpers/widgets/helper'
import type { WidgetComponentKey } from '@/views/dashboard.vue'

interface GraphState {
  isOpen: boolean
  isFullscreen: boolean
  isDetachedDialog: boolean
  removableDevices: boolean
  selectedDevices: WidgetDevice[]
  timePeriod: string
}

interface Dimensions {
  width: number
  height: number
}

export default defineComponent({
  name: 'HistoryWidget',
  components: {
    BaseWidget,
    HistoryGraph,
    HistoryMenuItems,
    ExportDataDialog
  },
  props: {
    widgetId: String,
    widgetType: String as PropType<WidgetComponentKey>,
    widgetMaxDevices: Object as PropType<Record<WidgetComponentKey, number>>,
    deviceIds: {
      type: Array as PropType<string[]>,
      default: (): string[] => []
    },
    width: Number,
    height: Number,
    settings: Object as PropType<WidgetSettings>
  },
  emits: ['fullscreenChange', 'devicesChanged', 'removeWidget', 'refreshDashboard'],
  setup(props) {
    const store = useStore<RootState>()
    const defaultParameter = computed<number | null>(
      () => store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
    )
    const options: MeasurementOptionsProps = reactive({
      ...toRefs(props as Required<typeof props>),
      defaultParameter
    })
    return useMeasurementOptions(options)
  },
  async mounted() {
    this.applyWidgetSettings()
    this.checkDevicesLoaded()
  },
  data() {
    return {
      accountId: null as string | null,
      exportData: false,
      graph: {
        isOpen: false,
        isFullscreen: false,
        isDetachedDialog: false,
        removableDevices: true,
        selectedDevices: [],
        timePeriod: 'day'
      } as GraphState,
      dimensions: {
        width: 0,
        height: 0
      } as Dimensions
    }
  },
  watch: {
    devicesLoaded() {
      this.checkDevicesLoaded()
    },
    selectedDevices() {
      this.updateDeviceList()
    }
  },
  computed: {
    deviceId(): string | undefined {
      return this.deviceIds?.[0]
    },
    devicesLoaded(): boolean {
      const isLoaded = this.deviceIds.map((id) => {
        const name =
          this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](id)
        return !!name
      })
      return !isLoaded.includes(false)
    },
    name(): string {
      return this.deviceIds.length === 1
        ? this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](
            this.deviceId
          )
        : this.$t('widget.title.multiple')
    },
    deviceType(): string | undefined {
      if (this.deviceIds.length === 1) {
        const deviceModel = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
        ](this.deviceId)
        const modelKey =
          this.$store.getters[`${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`](
            deviceModel
          )
        return deviceModelDisplay(modelKey, this.$t.bind(this))
      }
      return undefined
    },
    permissions(): Record<string, string[]> {
      const perms: Record<string, string[]> = {}
      for (const device of this.selectedDevices) {
        perms[device.id] = getPermissionsByDeviceId(device.id)
      }
      return perms
    }
  },
  methods: {
    async initWidget(): Promise<void> {
      this.applyWidgetSettings()
      this.updateDeviceList()
      this.graph.selectedDevices = this.selectedDevices

      // Note: nextTick is required, so that HistoryGraph is initialized with
      // open set to false, and then changed to true. This allows the graph to
      // know when it is opened and to load data for visualization
      await nextTick()
      this.graph.isOpen = true
    },
    checkDevicesLoaded(): void {
      // initialize once the devices exist
      if (this.devicesLoaded) {
        this.initWidget()
      }
    },
    parameterChange(newValue: number): void {
      this.selectedParameter = newValue
    },
    fullscreenChange(newValue: boolean): void {
      this.graph.isFullscreen = newValue
      this.$emit('fullscreenChange', this.graph.isFullscreen)
    },
    applyWidgetSettings(): void {
      if (this.settings) {
        const { timePeriod } = this.settings
        if (timePeriod) this.graph.timePeriod = timePeriod
      }
    },
    updateTimePeriod(timePeriod: string): void {
      if (timePeriod) {
        this.graph.timePeriod = timePeriod
        saveWidgetSettings(this.widgetId, { timePeriod })
      }
    },
    removeDevice(deviceIndex: number, options?: unknown): void {
      const deviceIds = [...this.deviceIds]
      deviceIds.splice(deviceIndex, 1)
      this.updateWidgetDevices(deviceIds)
      if (deviceIds.length === 0) {
        this.removeWidget(options)
      }
    },
    updateWidgetDevices(deviceIds: string[]): void {
      this.$emit('devicesChanged', deviceIds)
    },
    removeWidget(options?: unknown): void {
      this.graph.isOpen = false
      this.$emit('removeWidget', options)
    },
    updateWidgetSize(dims: Dimensions): void {
      this.dimensions = dims
    },
    refreshDashboard(): void {
      this.$emit('refreshDashboard')
    },
    refresh(): void {
      ;(this.$refs.historyGraph as InstanceType<typeof HistoryGraph>).loadData()
    },
    updateDeviceList(): void {
      this.graph.selectedDevices = this.selectedDevices
    },
    openExportData() {
      this.exportData = true
    },
    closeExportData() {
      this.exportData = false
    }
  }
})
</script>
<style lang="scss" scoped>
.history-widget {
  @media (max-width: 320px) {
    height: 500px;
  }
  .history-widget-content {
    padding: 0;
    margin: -12px;
  }
}
.toolbar {
  padding-left: 6px;
  .v-btn {
    min-width: 10px !important;
    padding: 0 0.4rem !important;
  }
}
</style>
